import React, { useEffect, useRef, useState } from "react";

import { Node, TransitionLine } from "../../objects.js";

import utils from "../../utils.js";
import { backgroundGrid } from "../../styles.js";

//#region ZOMM
const MAX_ZOOM = 1.5;
const MIN_ZOOM = 0.5;
const SCROLL_SENSITIVITY = 0.0005;
//#endregion

//#region PANNING
let dragStart = { x: 0, y: 0 };
let mousePosition = { x: 0, y: 0 };
//#endregion

let nodes = [];
let transitionLines = [];
let transitionPlaceholder = null;

export default function Canvas({
	setCanvas,
	alphabet,
	states,
	transitions,
	initialStateID,
	acceptStatesIDs,
	whenClicked,
	whenClickedNode,
	whenClickedControlPoint,
	whenMovedNode,
	whenMovedControlPoint,
	whenCreatedNewNode,
	whenCreatedNewTransition,
	whenEditing,
	whenContextMenu,
	creatingTransition,
}) {
	const [context, setContext] = useState(false);

	const [shifting, setShifting] = useState(false);
	const [dragging, setDragging] = useState(false);
	const [cameraZoom, setCameraZoom] = useState(1);
	const [gridOffset, setGridOffset] = useState({ x: 0, y: 0 });
	const [canvasSize, setCanvasSize] = useState({ x: 0, y: 0 });

	const [cameraOffset, setCameraOffset] = useState({ x: 0, y: 0 });

	// Used for moving items and creating transitions
	const [selectedItem, setSelectedItem] = useState({ index: -1, type: null });
	// Used for editing items
	const [focusedItem, setFocusedItem] = useState({
		index: -1,
		type: null,
		x: 0,
		y: 0,
	});

	const canvas = useRef();

	useEffect(() => {
		if (canvas.current) {
			setContext(canvas.current.getContext("2d"));
			setCanvas(canvas.current);
		}
	}, [canvas]);

	useEffect(() => {
		if (context) {
			SetUpCanvas();
			SetUpEvents();
			DrawCanvas();
		}
	}, [context]);

	useEffect(() => {
		if (context) {
			UpdateCanvas();
		}
	}, [alphabet, states, transitions, initialStateID, acceptStatesIDs]);

	useEffect(() => {
		if (context) {
			RedrawCanvas();
		}
	}, [cameraZoom, gridOffset, canvasSize]);

	//#region SET UP
	function SetUpCanvas() {
		canvas.current.width = canvas.current.offsetWidth;
		canvas.current.height = canvas.current.offsetHeight;

		setCameraOffset({
			x: canvas.current.width / 2,
			y: canvas.current.height / 2,
		});
	}
	function SetUpEvents() {
		window.addEventListener("keydown", onKeyDown);
		window.addEventListener("keyup", onKeyUp);

		window.addEventListener("resize", onWindowResize);
	}
	function PrepareCanvas() {
		canvas.current.width = canvas.current.offsetWidth;
		canvas.current.height = canvas.current.offsetHeight;

		context.scale(cameraZoom, cameraZoom);
		context.translate(cameraOffset.x, cameraOffset.y);

		let [x, y] = StoW(0, 0);
		context.clearRect(x, y, canvasSize.x, canvasSize.y);
	}
	//#endregion

	//#region EVENTS

	function handleClick(event) {
		if (event.detail === 2) {
			onPointerDoubleClick(event);
		}
	}

	function onPointerDown(event) {
		event.preventDefault();
		whenClicked();

		// Panning with scroll wheel button
		if (event.button === 1) {
			deSelect();
			StartPanning();
			return;
		}

		// Only if left click
		if (event.button !== 0) return;
		deSelect();
		deFocus();

		const element = ClickedElement();
		if (!element) {
			// if (creatingTransition) {
			// 	const newNode = CreateNewNode(mousePosition.x, mousePosition.y);
			// 	whenCreatedNewTransition(focusedItem.id, newNode.id);
			// }
			return;
		} else if (element.type == utils.NODE) {
			if (shifting && selectedItem.id >= 0) {
				whenCreatedNewTransition(selectedItem.id, element.id);
			} else if (creatingTransition) {
				whenCreatedNewTransition(focusedItem.id, element.id);
			}
			whenClickedNode(element.id);
		} else if (element.type == utils.CONTROL_POINT) {
			whenClickedControlPoint(element.id);
		}
		setSelectedItem({ ...element });
	}
	function onPointerDoubleClick(event) {
		event.preventDefault();
		GetPointerPosition(event);
		const [pointerWX, pointerWY] = StoW(mousePosition.x, mousePosition.y);

		const element = ClickedElement();
		if (element) {
			setFocusedItem({ ...element, x: pointerWX, y: pointerWY });
			whenEditing(mousePosition.x, mousePosition.y, element);
		} else {
			CreateNewNode(mousePosition.x, mousePosition.y);
		}
	}

	function onPointerMove(event) {
		event.preventDefault();
		GetPointerPosition(event);

		if (dragging) {
			setCameraOffset({
				x: mousePosition.x / cameraZoom - dragStart.x,
				y: mousePosition.y / cameraZoom - dragStart.y,
			});
		} else if (!shifting && selectedItem.id >= 0) {
			let [x, y] = StoW(mousePosition.x, mousePosition.y);

			switch (selectedItem.type) {
				case utils.NODE:
					whenMovedNode(selectedItem.id, x, y);
					break;

				case utils.CONTROL_POINT:
					whenMovedControlPoint(selectedItem.id, x, y);
					break;

				default:
					break;
			}
		} else if (creatingTransition) {
			DrawTransitionPlaceholder();
		}

		RedrawCanvas();
	}
	function onPointerUp(event) {
		event.preventDefault();

		deSelect();
		StopPanning();
	}

	function onKeyDown(event) {
		setShifting(event.shiftKey);
	}
	function onKeyUp(event) {
		setShifting(event.shiftKey);
		deSelect();
	}

	function onWindowResize(event) {
		UpdateCanvasSize();
	}

	function onContextMenu(event) {
		event.preventDefault();
		GetPointerPosition(event);
		const [pointerWX, pointerWY] = StoW(mousePosition.x, mousePosition.y);

		const element = ClickedElement();

		if (!element) {
			// Maybe do something...
			deFocus();
			return;
		}

		if (element.type == utils.NODE) {
			whenContextMenu({
				x: mousePosition.x,
				y: mousePosition.y,
				initial: nodes[utils.IDToIndex(nodes, element.id)].initial,
				accept: nodes[utils.IDToIndex(nodes, element.id)].accept,
				...element,
			});
		} else if (element.type == utils.CONTROL_POINT) {
			whenContextMenu({
				x: mousePosition.x,
				y: mousePosition.y,
				...element,
			});
		}
		setFocusedItem({ ...element, x: pointerWX, y: pointerWY });
	}
	//#endregion

	//#region DRAWING
	function DrawCanvas() {
		PrepareCanvas();
		DrawBackgroundGrid();
		DrawTransitionLines(0); // An initial drawing of all transitions
		DrawNodes(0); // An initial drawing of all states
	}
	function DrawBackgroundGrid() {
		//#region BACKGROUND (For screenshots)
		context.beginPath();
		const [x, y] = StoW(
			canvas.current.style.top,
			canvas.current.style.left
		);
		context.rect(
			x,
			y,
			canvasSize.x / cameraZoom,
			canvasSize.y / cameraZoom
		);
		context.fillStyle = backgroundGrid.fill;
		context.fill();
		context.closePath();
		//#endregion

		// Works fine if you don't get too close
		// Keep zoom under 1.5x

		let verticalCount = Math.floor(
			((canvasSize.x * 2) / backgroundGrid.size) * (4 / cameraZoom)
		);
		let horizontalCount = Math.floor(
			((canvasSize.y * 2) / backgroundGrid.size) * (4 / cameraZoom)
		);

		let [startX, startY] = StoW(0, 0);
		let [endX, endY] = StoW(canvasSize.x, canvasSize.y);

		for (let i = 0; i < verticalCount; i++) {
			context.beginPath();
			context.lineWidth = 1;
			context.strokeStyle = backgroundGrid.color;

			let displacement = i * backgroundGrid.size;
			let offset =
				Math.floor(gridOffset.y / backgroundGrid.size) *
				backgroundGrid.size;

			context.moveTo(startX, -canvasSize.y * 2 + displacement - offset);
			context.lineTo(endX, -canvasSize.y * 2 + displacement - offset);

			context.stroke();
			context.closePath();
		}

		for (let i = 0; i < horizontalCount; i++) {
			context.beginPath();
			context.lineWidth = 1;
			context.strokeStyle = backgroundGrid.color;

			let displacement = i * backgroundGrid.size;
			let offset =
				Math.floor(gridOffset.x / backgroundGrid.size) *
				backgroundGrid.size;

			context.moveTo(-canvasSize.y * 2 + displacement - offset, startY);
			context.lineTo(-canvasSize.y * 2 + displacement - offset, endY);

			context.stroke();
			context.closePath();
		}
	}

	function DrawNodes(startIndex) {
		nodes.splice(startIndex, nodes.length);
		states.forEach((state, index) => {
			if (index >= startIndex) {
				const isInitial = state.id === initialStateID;
				const isAccept =
					acceptStatesIDs.findIndex((id) => id === state.id) != -1;

				let node = new Node(
					state.x,
					state.y,
					state.id,
					state.name,
					isInitial,
					isAccept
				);
				nodes.push(node);
				node.Draw(context);
			}
		});
	}
	function DrawTransitionLines(startIndex) {
		// Drawing a line for each transition from each node
		transitionLines.splice(startIndex, transitionLines.length);

		transitions.forEach((transition, index) => {
			if (index >= startIndex) {
				const startingNode =
					states[utils.IDToIndex(states, transition.start)];
				const endingNode =
					states[utils.IDToIndex(states, transition.end)];

				const [controlPointX, controlPointY] =
					utils.getControlPointPosition(
						startingNode.x,
						startingNode.y,
						endingNode.x,
						endingNode.y
					);

				whenMovedControlPoint(
					transition.id,
					controlPointX,
					controlPointY
				);

				let transitionLine = new TransitionLine(
					transition.id,
					transition.start,
					transition.end,
					startingNode.x,
					startingNode.y,
					endingNode.x,
					endingNode.y,
					controlPointX,
					controlPointY,
					transition.symbols
				);
				transitionLines.push(transitionLine);
				transitionLine.Draw(context);
			}
		});
	}
	function DrawTransitionPlaceholder() {
		if (!creatingTransition) {
			transitionPlaceholder = null;
			return;
		}

		const [pointerWX, pointerWY] = StoW(mousePosition.x, mousePosition.y);

		const [controlPointX, controlPointY] = utils.getControlPointPosition(
			focusedItem.x,
			focusedItem.y,
			pointerWX,
			pointerWY
		);
		transitionPlaceholder = new TransitionLine(
			-1,
			-1,
			-1,
			focusedItem.x,
			focusedItem.y,
			pointerWX,
			pointerWY,
			controlPointX,
			controlPointY,
			"",
			true
		);
		transitionPlaceholder.Draw(context);
	}
	//#endregion

	//#region UPDATE
	function UpdateCanvasSize() {
		setCanvasSize({ x: canvas.current.width, y: canvas.current.height });
	}

	function UpdateCanvas() {
		PrepareCanvas();
		DrawBackgroundGrid();
		DrawTransitionPlaceholder();
		UpdateTransitionLines();
		UpdateNodes();
	}

	function UpdateNodes() {
		nodes = nodes.filter((node) => {
			const index = utils.IDToIndex(states, node.id);
			if (index === -1) return; // State was deleted

			UpdateNode(node, index);

			const clicked =
				(selectedItem.type === utils.NODE &&
					node.id === selectedItem.id) ||
				(focusedItem.type === utils.NODE && node.id === focusedItem.id);

			node.Draw(context, clicked);
			return true;
		});

		if (nodes.length < states.length) {
			// New states have been added
			DrawNodes(nodes.length);
		}
	}
	function UpdateNode(node, index) {
		node.name = states[index].name;
		node.x = states[index].x;
		node.y = states[index].y;
		node.initial = node.id === initialStateID;
		node.accept = acceptStatesIDs.findIndex((id) => id === node.id) != -1;
	}
	function UpdateTransitionLines() {
		// Drawing a line for each transition from each node
		transitionLines = transitionLines.filter((transition) => {
			const index = utils.IDToIndex(transitions, transition.id);
			if (index === -1) return; // Transition was deleted

			UpdateTransitionLine(transition, index);

			const clicked =
				(selectedItem.type === utils.CONTROL_POINT &&
					transition.id === selectedItem.id) ||
				(focusedItem.type === utils.CONTROL_POINT &&
					transition.id === focusedItem.id);
			transition.Draw(context, clicked);
			return true;
		});

		if (transitionLines.length < transitions.length) {
			// New transition lines have been added
			DrawTransitionLines(transitionLines.length);
		}
	}
	function UpdateTransitionLine(transitionLine, index) {
		const transition = transitions[index];
		const startingNode =
			states[utils.IDToIndex(states, transitionLine.startNodeID)];
		const endingNode =
			states[utils.IDToIndex(states, transitionLine.endNodeID)];

		transitionLine.endXPos = endingNode.x;
		transitionLine.endYPos = endingNode.y;
		transitionLine.startXPos = startingNode.x;
		transitionLine.startYPos = startingNode.y;

		transitionLine.controlPointX = transition.x;
		transitionLine.controlPointY = transition.y;

		transitionLine.symbols = transition.symbols;
	}
	//#endregion

	//#region REDRAW

	function RedrawCanvas() {
		UpdateCanvasSize();

		PrepareCanvas();
		DrawBackgroundGrid();
		DrawTransitionPlaceholder();
		RedrawTransitionLines();
		RedrawNodes();
	}

	function RedrawNodes() {
		// Drawing a circle for each node
		nodes.forEach((node) => {
			const index = states.findIndex((state) => state.id === node.id);
			if (index === -1) return;

			const clicked =
				(selectedItem.type === utils.NODE &&
					node.id === selectedItem.id) ||
				(focusedItem.type === utils.NODE && node.id === focusedItem.id);
			node.Draw(context, clicked);
		});
	}

	function RedrawTransitionLines() {
		// Drawing a line for each transition from each node
		transitionLines.forEach((transition) => {
			const index = transitions.findIndex((t) => t.id === transition.id);
			if (index === -1) return;

			const clicked =
				(selectedItem.type === utils.CONTROL_POINT &&
					transition.id === selectedItem.id) ||
				(focusedItem.type === utils.CONTROL_POINT &&
					transition.id === focusedItem.id);
			transition.Draw(context, clicked);
		});
	}

	//#endregion

	//#region UTILS
	function GetPointerPosition(event) {
		const rect = canvas.current.getBoundingClientRect();
		let x = event.clientX - rect.left;
		let y = event.clientY - rect.top;
		mousePosition = { x: x, y: y };
	}

	function WtoS(wx, wy) {
		let sx = (wx + cameraOffset.x) * cameraZoom;
		let sy = (wy + cameraOffset.y) * cameraZoom;

		return [sx, sy];
	}
	function StoW(sx, sy) {
		let wx = sx / cameraZoom - cameraOffset.x;
		let wy = sy / cameraZoom - cameraOffset.y;

		return [wx, wy];
	}
	//#endregion

	//#region ZOOMING AND PANNING

	function StartPanning() {
		setDragging(true);

		dragStart = {
			x: mousePosition.x / cameraZoom - cameraOffset.x,
			y: mousePosition.y / cameraZoom - cameraOffset.y,
		};
	}

	function StopPanning() {
		setGridOffset({ ...cameraOffset });
		setDragging(false);
	}

	function AdjustZoom(event) {
		const zoomAmount = event.deltaY * SCROLL_SENSITIVITY;
		if (!dragging) {
			let newCameraZoom = cameraZoom - zoomAmount;
			newCameraZoom = Math.min(newCameraZoom, MAX_ZOOM);
			newCameraZoom = Math.max(newCameraZoom, MIN_ZOOM);

			setCameraZoom(newCameraZoom);
		}
	}

	//#endregion

	//#region COLLISION CHECKING
	function ClickedElement() {
		let id = ClickedNode();

		if (id >= 0) {
			return { id: id, type: utils.NODE };
		} else {
			id = ClickedControlPoint();
			if (id >= 0) {
				return { id: id, type: utils.CONTROL_POINT };
			}
		}
	}
	function ClickedNode() {
		let [x, y] = StoW(mousePosition.x, mousePosition.y);

		let clickedNodes = nodes.filter((node) =>
			utils.circlePointCollision(x, y, node.outerCircle)
		);
		if (clickedNodes.length > 0) {
			return clickedNodes[0].id;
		} else {
			return -1;
		}
	}
	function ClickedControlPoint() {
		let [x, y] = StoW(mousePosition.x, mousePosition.y);

		let clickedTransitions = transitionLines.filter((transition) =>
			utils.pointInHandle(x, y, transition.handle)
		);

		if (clickedTransitions.length > 0) {
			return clickedTransitions[0].id;
		} else {
			return -1;
		}
	}
	//#endregion

	//#region CREATING
	function CreateNewNode(x, y) {
		[x, y] = StoW(x, y);
		return whenCreatedNewNode(x, y);
	}
	//#endregion

	function deSelect() {
		if (shifting) return;
		setSelectedItem({ index: -1, type: null });
	}
	function deFocus() {
		setFocusedItem({ index: -1, type: null, x: 0, y: 0 });
		whenEditing(null);
	}

	return (
		<canvas
			className="canvas"
			ref={canvas}
			onClick={handleClick}
			onPointerDown={onPointerDown}
			onPointerUp={onPointerUp}
			onPointerMove={onPointerMove}
			onWheel={AdjustZoom}
			onContextMenu={onContextMenu}
		></canvas>
	);
}
